<template>
    <div class="chat-container">
        <div class="chat-messages">
            <div
                v-for="(msg, index) in mensagens"
                :key="index"
                :class=" [
                    'message',
                    msg.usuarioId === usuarioId ? 'my-message' : 'other-message'
                ]"
            >
                <div class="message-header">
                    <strong>{{ formatarNome(msg.usuario) }}</strong>
                    <div>
                        <small style="margin-right:4px;">
                            {{ formatarHora(msg.hora) }}
                        </small>
                        <span v-if="msg.usuarioId == usuarioId">
                            <i
                                v-if="msg.status === 0"
                                class="fas fa-check"
                                style="color: #123c6b;"
                                title="Enviada"
                            ></i>
                            <i
                                v-else
                                class="fas fa-check-double"
                                style="color: #123c6b;"
                                :title="
                                    'Lida em ' + formatarHora(msg.dataLeitura)
                                "
                            ></i>
                        </span>
                    </div>
                </div>

                <div class="message-content">
                    <div v-if="msg.texto && String(msg.texto).trim()" class="message-text">
                        {{ msg.texto }}
                    </div>
                    <img
                        v-if="msg.tipo === 1 && msg.conteudoImagem"
                        :src="msg.conteudoImagem"
                        class="chat-image"
                    />
                </div>
            </div>
        </div>

        <div class="message-input">
            <div class="input-container">
                <textarea
                    ref="messageInput"
                    v-model="mensagem"
                    placeholder="Digite sua mensagem"
                    @keydown="handleKeydown"
                    @input="ajustarAltura"
                    :disabled="!conexaoAtiva || enviandoMensagem"
                    @paste="handlePaste"
                    rows="1"
                ></textarea>
            </div>

            <div v-if="urlPrevia" class="image-preview">
                <div class="image-container">
                    <img :src="urlPrevia" alt="Prévia da imagem" />
                    <div v-if="enviandoImagem" class="upload-overlay">
                        <div class="upload-spinner"></div>
                        <span>Enviando...</span>
                    </div>
                </div>
                <button class="remove-image-btn" @click="removerImagem" :disabled="enviandoImagem || enviandoMensagem">❌</button>
            </div>

            <input
                ref="fileInput"
                type="file"
                accept="image/*"
                @change="handleFileSelect"
                style="display: none"
            />

            <button
                @click="abrirSeletorArquivo"
                :disabled="!conexaoAtiva || enviandoImagem || enviandoMensagem || imagemPendente"
                class="attach-btn"
                title="Anexar imagem"
            >
                <i class="fas fa-paperclip"></i>
            </button>

            <button
                @click="enviar"
                :disabled="
                    (!mensagem.trim() && !imagemPendente) || !conexaoAtiva || enviandoImagem || enviandoMensagem
                "
            >
                <span v-if="enviandoImagem">Enviando imagem...</span>
                <span v-else-if="enviandoMensagem">Enviando...</span>
                <span v-else>Enviar</span>
            </button>
        </div>
    </div>
</template>

<script>
import {
    conectar,
    enviarMensagem,
    marcarComoLida,
    desconectar,
    uploadImagem,
    recarregarConversa
} from './MensagensHub.js'

export default {
    name: 'Mensagens',

    props: {
        conversaId: { type: String, required: true },
        usuarioId: { type: String, required: true },
        usuario: { type: String, required: true }
    },

    data() {
        return {
            mensagens: [],
            mensagem: '',
            imagemPendente: null,
            urlPrevia: null,
            conexaoAtiva: false,
            errosConexao: 0,
            atraso: null,
            novasMensagens: 0,
            enviandoImagem: false,
            enviandoMensagem: false
        }
    },

    watch: {
        conversaId: {
            immediate: true,
            handler(novoId, antigoId) {
                if (!novoId) return
                // Se já tinha uma conexão, encerra antes de abrir outra
                if (antigoId) {
                    desconectar()
                }
                this.iniciarConexao()
            }
        }
    },

    mounted() {
        window.addEventListener('paste', this.handlePaste)
        this.$nextTick(() => {
            this.ajustarAltura()
        })
    },

    beforeDestroy() {
        window.removeEventListener('paste', this.handlePaste)
        desconectar()
        this.$emit('novas-mensagens', 0)
    },

    methods: {
        async iniciarConexao() {
            try {
                await conectar(
                    this.conversaId,
                    this.onReceber,
                    this.onCarregar,
                    this.onMensagemLida
                )
                this.conexaoAtiva = true
                this.errosConexao = 0
                this.marcarComoLidas()
            } catch (error) {
                console.error('Falha na conexão:', error)
                this.tentarReconectar()
            }
        },

        tentarReconectar() {
            this.conexaoAtiva = false
            this.errosConexao++
            const delay = Math.min(1000 * this.errosConexao, 10000)
            setTimeout(() => this.iniciarConexao(), delay)
        },

        handlePaste(event) {
            const items = (
                event.clipboardData || event.originalEvent.clipboardData
            ).items

            for (const item of items) {
                if (item.type.indexOf('image') !== -1) {
                    event.preventDefault()

                    const blob = item.getAsFile()
                    if (blob.size > 5 * 1024 * 1024) {
                        alert('Imagem muito grande. Tamanho máximo: 5MB')
                        return
                    }

                    // Comprime a imagem antes de armazenar
                    this.comprimirImagem(blob).then(compressedBlob => {
                        this.imagemPendente = compressedBlob
                        this.urlPrevia = URL.createObjectURL(compressedBlob)
                    }).catch(error => {
                        console.error('Erro ao comprimir imagem:', error)
                        // Fallback: usa a imagem original
                        this.imagemPendente = blob
                        this.urlPrevia = URL.createObjectURL(blob)
                    })
                    break
                }
            }
        },

        async comprimirImagem(file, maxWidth = 800, maxHeight = 600, quality = 0.8) {
            return new Promise((resolve) => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                const img = new Image()
                
                img.onload = () => {
                    // Calcula as novas dimensões mantendo a proporção
                    let { width, height } = img
                    
                    if (width > height) {
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width
                            width = maxWidth
                        }
                    } else {
                        if (height > maxHeight) {
                            width = (width * maxHeight) / height
                            height = maxHeight
                        }
                    }
                    
                    canvas.width = width
                    canvas.height = height
                    
                    // Desenha a imagem redimensionada
                    ctx.drawImage(img, 0, 0, width, height)
                    
                    // Converte para blob com compressão
                    canvas.toBlob(resolve, 'image/jpeg', quality)
                }
                
                img.src = URL.createObjectURL(file)
            })
        },

        removerImagem() {
            this.imagemPendente = null
            this.urlPrevia = null
        },

        abrirSeletorArquivo() {
            this.$refs.fileInput.click()
        },

        handleFileSelect(event) {
            const file = event.target.files[0]
            if (!file) return

            if (!file.type.startsWith('image/')) {
                alert('Por favor, selecione apenas arquivos de imagem.')
                return
            }

            if (file.size > 5 * 1024 * 1024) {
                alert('Imagem muito grande. Tamanho máximo: 5MB')
                return
            }

            // Comprime a imagem usando o mesmo método do paste
            this.comprimirImagem(file).then(compressedBlob => {
                this.imagemPendente = compressedBlob
                this.urlPrevia = URL.createObjectURL(compressedBlob)
            }).catch(error => {
                console.error('Erro ao comprimir imagem:', error)
                // Fallback: usa a imagem original
                this.imagemPendente = file
                this.urlPrevia = URL.createObjectURL(file)
            })

            // Limpa o input para permitir selecionar o mesmo arquivo novamente
            event.target.value = ''
        },

        ajustarAltura() {
            this.$nextTick(() => {
                const textarea = this.$refs.messageInput
                if (textarea) {
                    // Reset altura para calcular a nova
                    textarea.style.height = 'auto'
                    // Calcula a altura necessária (máximo 120px = ~5 linhas)
                    const maxHeight = 120
                    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
                    textarea.style.height = newHeight + 'px'
                }
            })
        },

        handleKeydown(event) {
            if (event.key === 'Enter') {
                if (event.shiftKey) {
                    return
                } else {
                    if (!this.enviandoMensagem && !this.enviandoImagem && 
                        (this.mensagem.trim() || this.imagemPendente)) {
                        event.preventDefault()
                        this.enviar()
                    } else {
                        event.preventDefault()
                    }
                }
            }
        },

        async enviar() {
            if (!this.conexaoAtiva) {
                console.warn('Tentativa de envio sem conexão ativa')
                return
            }
            
            if (this.enviandoMensagem) {
                return
            }
            
            let conteudoImagem = null

            if (this.imagemPendente) {
                this.enviandoImagem = true
                try {
                    const data = await uploadImagem(this.imagemPendente)
                    conteudoImagem = data.url
                } catch (error) {
                    console.error('Erro ao enviar imagem:', error)
                    alert('Falha ao fazer upload da imagem. Recarregando conversa...')
                    
                    // Recarrega a conversa em caso de erro no upload
                    try {
                        await recarregarConversa(this.conversaId)
                    } catch (reloadError) {
                        console.error('Erro ao recarregar conversa:', reloadError)
                    }
                    
                    return
                } finally {
                    this.enviandoImagem = false
                }
            }

            if (this.mensagem.trim() || conteudoImagem) {
                this.enviandoMensagem = true
                try {
                    await enviarMensagem(
                        this.conversaId,
                        this.usuario,
                        this.usuarioId,
                        this.mensagem.trim(),
                        conteudoImagem ? 1 : 0,
                        conteudoImagem
                    )
                    
                    this.mensagem = ''
                    this.removerImagem()
                    this.ajustarAltura()
                } catch (error) {
                    console.error('Erro ao enviar mensagem:', error)
                    if (error.message.includes('conexão') || error.message.includes('connection')) {
                        this.tentarReconectar()
                    }
                } finally {
                    this.enviandoMensagem = false
                }
            }
        },

        onReceber(usuario, usuarioId, msg, time, status, id, tipo = 0, conteudoImagem = null, leituras = []) {
            if (!this.conexaoAtiva) return

            if (!id || id === null || id === undefined) {
                console.error('Mensagem recebida sem ID válido:', { usuario, usuarioId, msg, id })
                return
            }

            const leituraUsuario = leituras?.find(l => l.usuarioId === this.usuarioId)
            const foiLidaPeloUsuario = !!leituraUsuario
            
            this.mensagens.push({
                id: String(id),
                usuario,
                usuarioId,
                texto: msg || '',
                hora: time,
                status: status || 0,
                tipo: tipo || 0,
                conteudoImagem,
                leituras: leituras || [],
                foiLidaPeloUsuario,
                dataLeitura: leituraUsuario?.dataLeitura || null
            })

            if (usuarioId !== this.usuarioId) {
                this.novasMensagens++
                this.$emit('novas-mensagens', this.novasMensagens)
                this.marcarComoLidas()
            }
            this.scrollBottom()
        },

        onCarregar(lista) {
            // Filtra mensagens que tenham ID válido
            this.mensagens = lista
                .filter(m => m.id !== null && m.id !== undefined)
                .map(m => {
                    const leituraUsuario = m.leituras?.find(l => l.usuarioId === this.usuarioId)
                    const foiLidaPeloUsuario = !!leituraUsuario
                    
                    // Melhora o cálculo do status de exibição
                    let statusExibicao = 0 // Padrão: enviada (single check)
                    
                    if (m.usuarioId === this.usuarioId) { // Só mostra status para mensagens próprias
                        if (m.leituras && m.leituras.length > 0) {
                            // Se tem leituras de outros usuários, mostra double-check
                            const leituraOutroUsuario = m.leituras.find(l => l.usuarioId !== this.usuarioId)
                            statusExibicao = leituraOutroUsuario ? 1 : 0
                        }
                    }
                    
                    return {
                        id: String(m.id),
                        usuario: m.usuario,
                        usuarioId: m.usuarioId,
                        texto: String(m.texto || ''),
                        hora: m.dataEnvio,
                        status: statusExibicao,
                        dataLeitura: leituraUsuario?.dataLeitura || null,
                        tipo: m.tipo,
                        conteudoImagem: m.conteudoImagem || null,
                        leituras: m.leituras || [],
                        foiLidaPeloUsuario
                    }
                })
                
            this.calcularNovasMensagens()
            this.scrollBottom()
        },

        onMensagemLida(notificacao) {
            if (!this.conexaoAtiva) return

            // Busca as mensagens atualizadas (pode vir como "Mensagens" ou "mensagens")
            const mensagensAtualizadas = notificacao.Mensagens || notificacao.mensagens

            if (!mensagensAtualizadas || !Array.isArray(mensagensAtualizadas)) {
                console.warn('Dados de mensagens inválidos:', notificacao)
                return
            }

            // Atualiza o status das mensagens correspondentes
            this.mensagens = this.mensagens.map(mensagem => {
                const mensagemAtualizada = mensagensAtualizadas.find(ma => 
                    String(ma.Id || ma.id) === String(mensagem.id)
                )
                
                if (mensagemAtualizada) {
                    return {
                        ...mensagem,
                        status: mensagemAtualizada.Status || mensagemAtualizada.status || 1, // 1 = lida (double-check)
                        dataLeitura: mensagemAtualizada.DataLeitura || mensagemAtualizada.dataLeitura || new Date().toISOString()
                    }
                }
                
                return mensagem
            })
            
            // Força atualização da interface
            this.$forceUpdate()
        },

        async marcarComoLidas() {
            const naoLidas = this.mensagens.filter(
                m => m.usuarioId !== this.usuarioId && !m.foiLidaPeloUsuario && m.id // ← Adiciona verificação de ID
            )
            
            if (!naoLidas.length) return

            // Filtra apenas IDs válidos e converte para string
            const ids = naoLidas
                .map(m => m.id)
                .filter(id => id !== null && id !== undefined && id !== '') // ← Filtra IDs inválidos
                .map(id => String(id))

            // Só chama se tiver IDs válidos
            if (ids.length === 0) {
                console.warn('Nenhuma mensagem válida para marcar como lida')
                return
            }

            clearTimeout(this.atraso)
            this.atraso = setTimeout(async () => {
                try {
                    await marcarComoLida(this.conversaId, this.usuarioId, ids)
                    
                    this.$emit('mensagens-marcadas-como-lidas', {
                        conversaId: this.conversaId,
                        quantidadeMarcada: ids.length
                    })
                } catch (error) {
                    console.error('Erro ao marcar como lida:', error)
                    if (error.message.includes('conexão')) {
                        this.tentarReconectar()
                    }
                }
            }, 300)
        },

        scrollBottom() {
            this.$nextTick(() => {
                const el = this.$el.querySelector('.chat-messages')
                if (el) el.scrollTop = el.scrollHeight
            })
        },

        formatarHora(iso) {
            if (!iso) return ''
            const d = new Date(iso)
            if (isNaN(d.getTime())) return ''
            return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString(
                'pt-BR',
                {
                    hour: '2-digit',
                    minute: '2-digit'
                }
            )}`
        },

        formatarNome(nome) {
            if (!nome) return ''
            const p = nome.split(' ')
            return p.length < 2 ? p[0] : `${p[0]} ${p[p.length - 1]}`
        },

        calcularNovasMensagens() {
            this.novasMensagens = this.mensagens.filter(
                m => m.usuarioId !== this.usuarioId && !m.foiLidaPeloUsuario
            ).length

            this.$emit('novas-mensagens', this.novasMensagens)
        }
    }
}
</script>

<style scoped>
.chat-container {
    max-width: 600px;
    margin: 0 auto;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    height: 80vh;
    font-family: "Inter", "Segoe UI", sans-serif;
    background-color: #fafafa;
    box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #fff;
    border-bottom: 1px solid #eee;
}

.message {
    margin-bottom: 16px;
    padding: 10px 14px;
    border-radius: 12px;
    max-width: 75%;
    font-size: 14px;
    line-height: 1.4;
    word-wrap: break-word;
    transition: background 0.2s ease;
}

.message-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 12px;
    color: #242424;
}

.message-content {
    font-size: 14px;
    color: #272727;
}

.message-text {
    margin-bottom: 6px;
    word-wrap: break-word;
}

.my-message {
    background-color: #e8f3ff;
    margin-left: auto;
    border: 1px solid #d0e5ff;
    border-top-right-radius: 4px;
}

.other-message {
    background-color: #f5f5f5;
    margin-right: auto;
    border: 1px solid #e0e0e0;
    border-top-left-radius: 4px;
}

.chat-image {
    max-width: 240px;
    max-height: 240px;
    border-radius: 8px;
    margin-top: 6px;
    border: 1px solid #ddd;
}

/* Input e barra inferior */
button {
    padding: 10px 18px;
    background-color: #20a8d8;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s ease;
}

button:disabled {
    background: #20a7d8cc;
    cursor: not-allowed;
}

button:hover:enabled {
    background-color: #20a8d8;
}

.message-input {
    display: flex;
    align-items: flex-end;
    padding: 10px;
    background: #fff;
    gap: 10px; /* espaço entre os elementos */
}

.input-container {
    flex-grow: 1;
}

.input-container textarea {
    display: flex;
    align-items: flex-end;
    width: 100%;
    min-height: 43px;
    max-height: 120px;
    padding: 12px 16px;
    border: 1px solid #ddd;
    border-radius: 5px;
    outline: none;
    font-size: 14px;
    font-family: inherit;
    background: #fafafa;
    transition: border 0.2s ease, background 0.2s ease;
    resize: none;
    overflow-y: auto;
    line-height: 1.4;
    margin: 0;
}

.input-container textarea:focus {
    border-color: #4a90e2;
    background: #fff;
}

/* Pré-visualização da imagem agora lado a lado */
.image-preview {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    padding: 4px 6px;
}

.image-container {
    position: relative;
}

.image-preview img {
    max-height: 40px;
    max-width: 60px;
    border-radius: 6px;
    border: 1px solid #ddd;
    display: block;
}

.upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: white;
    font-size: 10px;
}

.upload-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #ffffff33;
    border-top: 2px solid #ffffff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 2px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.remove-image-btn {
    background: transparent;
    border: none;
    font-size: 13px;
    cursor: pointer;
}

.remove-image-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.attach-btn {
    background: #f5f5f5;
    color: #666;
    border: 1px solid #ddd;
    min-width: 43px;
    height: 43px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.attach-btn:hover:enabled {
    background: #e9e9e9;
    color: #333;
    border-color: #bbb;
}

.attach-btn:disabled {
    background: #f9f9f9;
    color: #ccc;
    border-color: #e5e5e5;
    cursor: not-allowed;
}
</style>
